import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import { Loading, Button } from 'components/patterns'
import { getTruckFeatures } from 'selectors/truck'
import { assoc, propOr, sum } from 'ramda'
import strings from 'utils/strings'
import ConflictsOverView from '../ConflictsOverView'
import {
  validateAndGetConflicts,
  getUnresolvedConflictsCounts,
  getConflictingFeaturesThatWillBeRemoved,
  getRejectedFeatureKeys,
  pruneRejectedFeaturesFromConflicts,
} from '../utils'
import {
  Footer,
  StyledConflictsResolveView,
  AddCollectionBlurb,
} from './FeatureCollections.style'
import { CONFLICTS_OVER_VIEW, CONFLICTS_TABLE_VIEWS } from '../constants'

// Returns the index of the first unresolved pin in the flat pin array for the
// given conflict type. ConflictsResolveView uses this as a direct array index
// into modulePinConflicts, so the step must point at the first pin that still
// needs a selection — not just the count of resolved pins.
//
// Counting resolved pins was the previous approach, which assumed all resolved
// pins are a contiguous prefix. Cascading auto-resolution breaks that assumption:
// a later pin can be auto-resolved while an earlier pin remains unresolved,
// causing the count to point at the wrong (already-resolved) row.
// ConflictsResolveView filters modulePinConflicts to only unresolved pins,
// so the current conflict is always at index 0 regardless of how many
// earlier pins were auto-resolved.
const getCurrentConflictStep = () => 0

/**
 * Build the ongoing feature set after conflict resolution.
 *
 * Newly-selected features that lost a conflict are excluded entirely —
 * they were never on the vehicle, so no removal request should be sent to the API.
 *
 * Existing vehicle features that lost a conflict are marked for deletion —
 * they ARE on the vehicle and the API must be told to remove them.
 */
export const createReviewFeaturesList = (
  selectedFeatures,
  features,
  conflicts,
  originalConflicts = null
) => {
  const featuresToBeRemoved = getConflictingFeaturesThatWillBeRemoved(
    conflicts,
    originalConflicts
  )
  const removedIds = new Set(featuresToBeRemoved.map((f) => f.featureId))

  // Newly-selected features that lost the conflict are excluded —
  // never on the vehicle, so no removal API call is needed.
  const featuresMarkedForAddition = selectedFeatures
    .filter((f) => !removedIds.has(f.id))
    .map(assoc('isMarkedForAddition', true))

  // Existing vehicle features that lost the conflict are marked for deletion.
  // Exception: if the feature was already staged as an addition in a prior
  // conflict-resolution pass (isMarkedForAddition: true), it was never
  // persisted to the backend, so it must be excluded entirely rather than
  // marked for deletion — sending a removal for a non-existent item would
  // produce a contradictory API payload.
  const resolvedExistingSet = features
    .filter(
      (feature) => !(removedIds.has(feature.id) && feature.isMarkedForAddition)
    )
    .map((feature) =>
      removedIds.has(feature.id)
        ? assoc('isMarkedForDeletion', true, feature)
        : feature
    )

  return [...featuresMarkedForAddition, ...resolvedExistingSet]
}

const ResolveConflicts = ({
  serialNumber,
  changeStep,
  selectedFeatures,
  setIoSwitches,
  setReviewFeatures,
  onGoingSet,
  disabledMessage,
}) => {
  const [view, setView] = useState(CONFLICTS_OVER_VIEW)

  const dispatch = useDispatch()
  const [conflicts, setConflicts] = useState(null)
  const [currentConflictSelection, setCurrentConflictSelection] = useState(null)
  const [currentConflictType, setCurrentConflictType] = useState(null)
  // Pristine conflict state from the API — never mutated by user confirmations.
  const originalConflictsRef = useRef(null)

  const features = useSelector(getTruckFeatures, { serialNumber })

  const nextSet = useMemo(
    () => [
      ...selectedFeatures.map(assoc('isMarkedForAddition', true)),
      ...(onGoingSet || features || []),
    ],
    [selectedFeatures, features, onGoingSet]
  )

  useEffect(() => {
    if (conflicts === null) {
      const checkValidation = async () => {
        const { currentConflictingFeatures = false } =
          await validateAndGetConflicts(dispatch, serialNumber, nextSet)
        setConflicts(currentConflictingFeatures)
        // Capture once — never overwritten by confirmations.
        if (originalConflictsRef.current === null) {
          originalConflictsRef.current = currentConflictingFeatures
        }
      }

      checkValidation().catch(console.error)
    }
  }, [dispatch, nextSet, serialNumber, conflicts])

  const handleReviewFeatures = () => {
    setIoSwitches({})
    const updatedOnGoingSet = createReviewFeaturesList(
      selectedFeatures,
      onGoingSet || features,
      conflicts,
      originalConflictsRef.current
    )
    setReviewFeatures(updatedOnGoingSet)
    changeStep(3)
  }

  const handleChangeConflictView = (nextView) => {
    if (nextView === CONFLICTS_OVER_VIEW) {
      setView(nextView)
    } else {
      setCurrentConflictType(nextView)
      setView(CONFLICTS_TABLE_VIEWS)
    }
  }

  const handleOnSelect = (module, pin, type, selection) => {
    setCurrentConflictSelection([module, pin, type, selection])
  }

  const handleConfirmSelection = () => {
    const [module, pin, type, selection] = currentConflictSelection

    // Use the original pin state (pre-pruning) when computing the rejection set.
    // If option B was removed from this pin by an earlier confirmation, the
    // current pin state no longer contains B's features. By sourcing from the
    // original pin we ensure B's bystander features are included in rejectedKeys
    // and can cascade to other unresolved pins during this confirmation.
    const originalPin = originalConflictsRef.current?.[type]?.[module]?.[pin]
    const sourcePin =
      originalPin && originalPin.selection === null
        ? originalPin
        : conflicts[type][module][pin]
    const resolvedPin = { ...sourcePin, selection }
    const withSelection = {
      ...conflicts,
      [type]: {
        ...conflicts[type],
        [module]: { ...conflicts[type][module], [pin]: resolvedPin },
      },
    }
    const rejectedKeys = new Set(getRejectedFeatureKeys(resolvedPin))
    const propagated = pruneRejectedFeaturesFromConflicts(
      withSelection,
      rejectedKeys,
      originalConflictsRef.current
    )
    setConflicts(propagated)
    setCurrentConflictSelection(null)

    const hasUnresolved = Object.values(
      propagated[currentConflictType] || {}
    ).some((moduleGroup) =>
      Object.values(moduleGroup).some((p) => p.selection === null)
    )
    if (!hasUnresolved) {
      setCurrentConflictType(null)
      setView(CONFLICTS_OVER_VIEW)
    }
  }

  if (conflicts === null) return <Loading />

  const unresolvedConflictsCount = getUnresolvedConflictsCounts(conflicts)

  const enableReviewFeatures = sum(unresolvedConflictsCount) === 0

  const conflictOverView = (
    <>
      <ConflictsOverView
        unresolvedConflictsCount={unresolvedConflictsCount}
        changeView={handleChangeConflictView}
        handleCancel={() => {}}
        numSwitchConflicts={0}
        hideHeader
        hideSwitchConflicts
      />
      <Footer>
        <Button
          text={strings.back_to_feature_collection}
          type="secondary"
          onClick={() => changeStep(1)}
        />
        <Button
          text={strings.review_features}
          onClick={handleReviewFeatures}
          disabled={!enableReviewFeatures}
        />
        {!enableReviewFeatures && (
          <AddCollectionBlurb>
            {disabledMessage || strings.collection_can_not_be_added}
          </AddCollectionBlurb>
        )}
      </Footer>
    </>
  )

  const currentConflictStep = getCurrentConflictStep(
    conflicts,
    currentConflictType
  )
  const disableConfirmSelection = !currentConflictSelection

  const conflictTableView = (
    <>
      <StyledConflictsResolveView
        conflicts={conflicts}
        view={currentConflictType}
        allFeatures={nextSet}
        onSelect={handleOnSelect}
        currentConflictSelection={propOr(null, 3, currentConflictSelection)}
        currentConflictStep={currentConflictStep}
      />
      <Footer>
        <Button
          text={strings.back_to_feature_collection}
          type="secondary"
          onClick={() => changeStep(1)}
        />
        <Button
          text={strings.confirm_selection}
          onClick={handleConfirmSelection}
          disabled={disableConfirmSelection}
        />
      </Footer>
    </>
  )

  const views = {
    [CONFLICTS_OVER_VIEW]: conflictOverView,
    [CONFLICTS_TABLE_VIEWS]: conflictTableView,
  }

  return <>{views[view]}</>
}

ResolveConflicts.propTypes = {
  serialNumber: PropTypes.string,
  changeStep: PropTypes.func,
  selectedFeatures: PropTypes.array,
  onGoingSet: PropTypes.array,
  setIoSwitches: PropTypes.func,
  setReviewFeatures: PropTypes.func,
  /** Override the blurb shown when conflicts are not yet fully resolved.
   *  Defaults to the collection-edit wording if omitted. */
  disabledMessage: PropTypes.string,
}

export default ResolveConflicts
